import clsx from 'clsx'
import type { ChangeEvent } from 'react'

import { FvButton, Icon, type IconName } from '@fv/client-components'
import type { NotificationFilter } from '@fv/models'

import YesNoToggle from '@/components/inputs/YesNoToggle'
import { List, ListItem } from '@/components/List'
import { Loading } from '@/components/Loading'
import { ShipperTooltip } from '@/components/ShipperTooltip'

import { useAccountFeatures } from '../auth'
import { useHasPermissionFn } from '../auth/hooks'
import { onlyUnread, pinnedFilter } from './constants'
import { useMuteNotificationTypes } from './mutations'
import { useNotificationCount, useUserNotificationSettings } from './queries'

type NavItemModel = {
  filter: NotificationFilter
  icon: IconName
  label: string
  transform?: string
  id: string
  canMute: boolean
}

interface NotificationsSideBarProps {
  filterId: string
  onFilterChange: (id: string, filter: NotificationFilter) => void
}

const vendor: NavItemModel = {
  filter: { types: ['vendor-created', 'vendor-booked'] },
  icon: 'warehouse',
  label: 'Vendor shipments',
  id: 'vendor-shipments',
  canMute: true,
}

const docCenterItem: NavItemModel = {
  filter: onlyUnread({
    types: ['doc-center:invalid-email', 'doc-center:email-finished'],
  }),
  icon: 'file-alt',
  label: 'Document center',
  id: 'doc-center',
  canMute: true,
}

const routeGuideItem: NavItemModel = {
  filter: onlyUnread({ types: ['route-guide:exhausted'] }),
  icon: 'ban',
  label: 'Route guides',
  id: 'route-guides',
  canMute: true,
}

const useNavItems = () => {
  const { vendorPortal, truckLoad } = useAccountFeatures()
  const hasPermission = useHasPermissionFn()
  const items: NavItemModel[] = [
    { filter: {}, icon: 'list', label: 'All', id: 'all', canMute: false },
    {
      filter: pinnedFilter,
      icon: 'thumbtack',
      label: 'Pinned',
      id: 'pinned',
      canMute: false,
    },
    {
      filter: onlyUnread({ types: ['missed-pickup'] }),
      icon: 'exclamation-triangle',
      label: 'Missed pickup',
      id: 'missed-pickup',
      canMute: true,
    },
    {
      filter: onlyUnread({ types: ['booking-error'] }),
      icon: 'exclamation-triangle',
      label: 'Booking error',
      id: 'booking-error',
      canMute: true,
    },
    {
      filter: onlyUnread({ types: ['rejection'] }),
      icon: 'exclamation-triangle',
      label: 'Rejections',
      id: 'rejection',
      canMute: true,
    },
    ...(vendorPortal ? [vendor] : []),
    ...(truckLoad ? [routeGuideItem] : []),
    {
      filter: onlyUnread({ types: ['tracking-change'] }),
      icon: 'truck-fast',
      label: 'Shipment tracking',
      id: 'shipment-tracking',
      canMute: true,
    },
    {
      filter: onlyUnread({ types: ['export', 'document-batch'] }),
      icon: 'download',
      label: 'Downloads',
      id: 'downloads',
      canMute: true,
    },
    {
      filter: onlyUnread({ types: ['load-upload:completed'] }),
      icon: 'file-alt',
      label: 'Shipment uploads',
      id: 'shipment-uploads',
      canMute: true,
    },
    ...(hasPermission('load-file-manager') ? [docCenterItem] : []),
  ]
  return items
}

export const NotificationsSideBar = ({
  filterId,
  onFilterChange,
}: NotificationsSideBarProps) => {
  const filterItems = useNavItems()
  const userSettingsQuery = useUserNotificationSettings()

  if (userSettingsQuery.isLoading) {
    return <Loading />
  }

  const handleSelect = (item: NavItemModel) => () => {
    const { onlyUnread, ...filter } = item.filter
    onFilterChange(item.id, filter)
  }
  return (
    <div className="border-r-fv-gray overflow-auto border-r bg-fv-blue-50 py-6">
      <List className="border-fv-blue-150 border-b">
        <ListItem className="pl-4">
          <Icon icon="bell-on" className="text-fv-gray-dark" />
          <span>Notifications</span>
        </ListItem>

        {filterItems.map(item => (
          <NavItem
            key={item.id}
            item={item}
            isActive={filterId === item.id}
            onClick={handleSelect(item)}
          />
        ))}
      </List>
    </div>
  )
}

type NavItemProps = {
  item: NavItemModel
  isActive: boolean
  onClick: () => void
}
const NavItem = ({ item, isActive, onClick }: NavItemProps) => {
  const unreadQuery = useNotificationCount(item.filter)
  const { data: userSettings } = useUserNotificationSettings()
  const muteMutation = useMuteNotificationTypes()

  const notificationTypes = item.filter.types || []
  const mutedTypes = userSettings?.mutedNotificationTypes ?? []
  const areAllMuted =
    notificationTypes.length > 0 &&
    notificationTypes.every(type => mutedTypes.includes(type))

  const handleToggleMuted = async (e: ChangeEvent<HTMLInputElement>) => {
    if (
      !areAllMuted &&
      !window.confirm(
        'By muting these notifications, all un-pinned notifications in this category will be deleted and you will not receive any new notifications for this category.',
      )
    ) {
      return
    }
    await muteMutation.mutateAsync({
      types: notificationTypes,
      muted: !e.target.checked,
    })
  }

  return (
    <ListItem
      custom
      className={clsx('!border-fv-blue-150', {
        'bg-fv-beer-light ': isActive,
      })}
    >
      {unreadQuery.isLoading && <Icon icon="spinner" />}
      {!unreadQuery.isLoading && (
        <div className="w-10 text-sm ml-3 bg-white rounded-md flex items-center justify-center -mr-2 border-2 border-fv-blue-150">
          {unreadQuery.data ?? 0}
        </div>
      )}
      <FvButton
        icon={item.icon}
        transform={item.transform}
        className="flex-1 text-left p-4 flex items-center"
        onClick={onClick}
      >
        {item.label}
      </FvButton>
      {item.canMute && (
        <ShipperTooltip
          label={`Turn ${areAllMuted ? 'on' : 'off'} these notifications`}
        >
          <span>
            <YesNoToggle
              onOff
              name={item.id}
              checked={!areAllMuted}
              onChange={handleToggleMuted}
              disabled={muteMutation.isPending}
              className="mr-4"
            />
          </span>
        </ShipperTooltip>
      )}
    </ListItem>
  )
}
